import { useEffect, useRef, useState } from "react";
import { ArrowRight, Search, Sparkles, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openAiChat } from "@/components/ai/aiChatBus";
import { useAiChatQuota } from "@/hooks/useAiChatQuota";
import { useAiChatConfig } from "@/hooks/useAiChatConfig";
import AiQuotaNotice from "@/components/ai/AiQuotaNotice";

const POPULAR_QUESTIONS = [
  "Vad är spotpriset just nu?",
  "Vilket elavtal är billigast idag?",
  "Är månadspris eller fast elpris bäst?",
  "Hur mycket kan jag spara med solceller?",
  "När på dygnet är elen billigast?",
  "Hur byter jag elbolag?",
];
/** Sparkle-knapp i headern som öppnar ett AI-sökfält och skickar frågan till AI-widgeten. */
const HeaderAskAi = () => {
  const { config, loading: configLoading } = useAiChatConfig();
  const [open, setOpen] = useState(false);
  const quota = useAiChatQuota(open);
  const [question, setQuestion] = useState("");
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [panelTop, setPanelTop] = useState("4rem");

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const place = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) setPanelTop(`${Math.round(rect.bottom + 8)}px`);
    };
    place();
    const onDown = (event: Event) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // AI-knappen har en egen inställning i adminpanelen, separat från
  // chatt-widgeten och chattsidan /elmarknad-ai.
  if (configLoading || !config.showHeaderButton) return null;

  const query = question.trim().toLowerCase();
  const suggestions = query
    ? POPULAR_QUESTIONS.filter((q) => q.toLowerCase().includes(query)).slice(0, 5)
    : POPULAR_QUESTIONS;

  const trigger = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    openAiChat(trimmed);
    setQuestion("");
    setHighlight(-1);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === "Enter" && highlight >= 0) {
      event.preventDefault();
      trigger(suggestions[highlight] ?? "");
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Fråga AI"
        aria-expanded={open}
        title="Fråga AI"
        onClick={() => setOpen((prev) => !prev)}
        className="h-9 w-9"
      >
        <Sparkles size={20} />
      </Button>

      {open && (
        <div
          ref={panelRef}
          className="fixed left-3 right-3 top-[calc(var(--ask-ai-top,4rem))] z-50 w-auto max-h-[70dvh] overflow-y-auto rounded-xl border border-border bg-card text-card-foreground shadow-xl lg:absolute lg:left-auto lg:right-0 lg:top-full lg:mt-2 lg:w-[26rem] lg:max-h-none"
          style={{ ["--ask-ai-top" as string]: panelTop }}
        >
          <form
            className="p-3"
            onSubmit={(event) => {
              event.preventDefault();
              trigger(question);
            }}
          >
            <div className="flex items-center gap-2 w-full border border-input rounded-full bg-card px-4 h-11 shadow-xs focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/30">
              <Sparkles size={18} className="text-primary shrink-0" />
              <input
                ref={inputRef}
                value={question}
                onChange={(event) => {
                  setQuestion(event.target.value);
                  setHighlight(-1);
                }}
                onKeyDown={onKeyDown}
                placeholder="Fråga AI om elpriser, elavtal…"
                aria-label="Din fråga till AI:n"
                className="flex-1 bg-transparent outline-hidden text-sm text-foreground placeholder:text-muted-foreground"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Stäng AI-sök"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </form>

          {suggestions.length > 0 && (
            <>
              <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {query ? <Search size={14} /> : <TrendingUp size={14} />}
                {query ? "Förslag" : "Populära frågor"}
              </div>
              <ul className="pb-2">
                {suggestions.map((suggestion, index) => (
                  <li key={suggestion}>
                    <button
                      type="button"
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => trigger(suggestion)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                        index === highlight ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                      }`}
                    >
                      <Sparkles size={14} className="shrink-0 text-primary" />
                      <span className="truncate">{suggestion}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border px-4 py-3">
                <AiQuotaNotice quota={quota} compact />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderAskAi;
